import { useEffect, useState } from "react";

import { useStore } from "@tanstack/react-form";
import { format, isValid, parse } from "date-fns";

import { ReactComponent as CalendarIcon } from "@assets/svg/calendar-filled.svg";

import { cn } from "@utils/ui";

import { useFieldContext } from "..";
import { Calendar } from "../../Calendar";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { getErrorMessage } from "../utils";
import FieldWrapper from "./FieldWrapper";

type DateFieldProps = {
  label?: string;
  subLabel?: string;
  labelClassName?: string;
  labelContainerClassName?: string;
  placeholder?: string;
  required?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
};

export const DateField = ({
  label,
  subLabel,
  labelClassName,
  labelContainerClassName,
  placeholder = "Select date",
  required,
  dateFormat = "yyyy-MM-dd",
  minDate,
  maxDate,
  className,
}: DateFieldProps) => {
  const field = useFieldContext<string>();
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date | null>(null);

  const errors = useStore(field.store, (state) => state.meta.errors);
  const hasError = errors.length > 0 && field.state.meta.isDirty;

  useEffect(() => {
    const parsedDate = field.state.value
      ? parse(field.state.value, dateFormat, new Date())
      : null;

    setCurrentDate(parsedDate);

    return () => {
      setCurrentDate(null);
    };
  }, [dateFormat, field.state.value]);

  const isValidDate = currentDate && isValid(currentDate);

  // Format date for display
  const displayValue = isValidDate ? format(currentDate, "MMM dd, yyyy") : "";

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    const formattedDate = format(date, dateFormat);
    field.handleChange(formattedDate);
    setIsOpen(false);
  };

  const handleInputChange = (value: string) => {
    // Parse the input value to a Date object
    const parsedDate = parse(value, dateFormat, new Date());
    if (isValid(parsedDate)) {
      field.handleChange(format(parsedDate, dateFormat));
    } else {
      field.handleChange("");
    }
  };

  return (
    <FieldWrapper
      id={field.name}
      label={label}
      subLabel={subLabel}
      labelClassName={labelClassName}
      labelContainerClassName={labelContainerClassName}
      isRequired={required}
      errorMessage={hasError ? getErrorMessage(errors[0]) : undefined}
      className="w-full"
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div
          className={cn(
            "shadow-xs flex min-h-11 w-full min-w-0 items-center gap-2 rounded-lg border border-border bg-transparent px-3 py-1 text-base outline-none transition-[color,box-shadow] dark:bg-input/30 md:text-sm",
            "selection:bg-primary selection:text-primary-foreground",
            "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
            "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
            "aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20",
            !field.state.value && "text-muted-foreground",
            className
          )}
          aria-invalid={hasError}
        >
          {displayValue ? (
            <Input
              type="date"
              value={field.state.value}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={field.handleBlur}
              className="min-h-min w-full border-none bg-none p-0 text-sm focus-visible:ring-0 [&::-webkit-calendar-picker-indicator]:hidden"
              min={minDate ? format(minDate, dateFormat) : undefined}
              max={maxDate ? format(maxDate, dateFormat) : undefined}
            />
          ) : (
            <PopoverTrigger className="flex-1 shrink-0 text-left">
              <span className="text-muted-foreground text-sm">{placeholder}</span>
            </PopoverTrigger>
          )}

          <PopoverTrigger>
            <CalendarIcon className="mr-2 h-4 w-4" />
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto bg-background-white p-0" align="end">
          <Calendar
            currentDate={currentDate}
            dateFormat={dateFormat}
            minDate={minDate}
            maxDate={maxDate}
            handleDateSelect={handleDateSelect}
          />
        </PopoverContent>
      </Popover>
    </FieldWrapper>
  );
};
